import { FC, useMemo, memo } from "react"
import classNames from "classnames"
import CellCardContent from "./CellCardContent"
import CellCardLarge from "./CellCardLarge"
import CellCardMedium from "./CellCardMedium"
import CellCardSmall from "./CellCardSmall"
import { useCellCardState } from "./useCellCardState"
import Tooltip from "@/components/shared/Tooltip"
import { TypefaceVariant } from "@/types/alert"
import { Cell } from "@/types/cell"
import { CardSize, Token } from "@/types/index"
import { isEqual } from "@/utils/lodash"
import "./index.scss"

export enum CellStatus {
   OCCUPIED = "occupied",
   VACANT = "vacant",
   DISCONNECTED = "disconnected",
   INMATE_TRANSFER = "inmate-transfer", // not used for now
   MAINTENANCE = "maintenance",
}

export enum UserStatus {
   MOVING = "moving",
   STABLE = "stable",
}

export interface CellCardProps {
   cellId: string
   bpm?: number
   bpmStabilityScore?: number
   rpm?: number
   rpmStabilityScore?: number
   occupied: boolean
   maintenance: boolean
   isCustom?: boolean
   hasDoorSensor?: boolean
   doorSensorChargeLevel?: number | null
   doorSensorChargeThreshold?: number
   doorSensorLowBattery?: boolean
   cellStatus: Cell["data"]["status"]
   userStatus: UserStatus
   typefaceColor?: keyof typeof TypefaceVariant
   hideButtons?: boolean
   className?: string
   isExpanded?: boolean
   isCollapsed?: boolean
   onExpandedChange?: (isExpanded: boolean) => void
   onClick?: () => void
   onSetVacant?: () => void
   onSetMaintenanceOff?: () => void
   onViewDetails?: () => void
   onTriggerTestAlarm?: () => void
}

const CellCard: FC<CellCardProps> = ({
   cellId,
   bpm,
   bpmStabilityScore,
   rpm,
   rpmStabilityScore,
   occupied,
   maintenance,
   isCustom,
   hasDoorSensor,
   doorSensorChargeLevel,
   doorSensorChargeThreshold = 20,
   cellStatus: cell,
   userStatus,
   typefaceColor,
   hideButtons = false,
   isExpanded = false,
   isCollapsed = false,
   onExpandedChange,
   onClick,
   onSetVacant,
   onSetMaintenanceOff,
   onViewDetails,
   onTriggerTestAlarm,
}) => {
   const doorSensorLowBattery = useMemo(
      () =>
         !!hasDoorSensor &&
         !!doorSensorChargeLevel &&
         doorSensorChargeLevel <= doorSensorChargeThreshold,
      [hasDoorSensor, doorSensorChargeLevel, doorSensorChargeThreshold]
   )

   const size =
      (localStorage.getItem(Token.CELL_CARD_SIZE) as CardSize) ?? CardSize.LARGE

   const { cellStatus, noVitals, isMoving, humanPresenceInVacant } =
      useCellCardState({
         cellStatus: cell,
         occupied,
         maintenance,
         typefaceColor,
         userStatus,
      })

   const showCellIcon =
      [CellStatus.OCCUPIED, CellStatus.VACANT].includes(cellStatus) &&
      !isMoving &&
      !humanPresenceInVacant

   const hasAction = useMemo(() => {
      if (hideButtons) return false
      return noVitals || maintenance
   }, [noVitals, maintenance, hideButtons])

   const isClickable = useMemo(
      () => size !== CardSize.LARGE || !!onClick,
      [size, onClick]
   )

   const largeCellCard = (
      <CellCardLarge
         cellId={cellId}
         bpm={bpm}
         bpmStabilityScore={bpmStabilityScore}
         rpm={rpm}
         rpmStabilityScore={rpmStabilityScore}
         occupied={occupied}
         maintenance={maintenance}
         isCustom={isCustom}
         hasDoorSensor={hasDoorSensor}
         doorSensorLowBattery={doorSensorLowBattery}
         cellStatus={cell}
         userStatus={userStatus}
         typefaceColor={typefaceColor}
         hideButtons={hideButtons}
         isExpanded={isExpanded}
         onClick={() => onExpandedChange?.(false)}
         onSetVacant={onSetVacant}
         onSetMaintenanceOff={onSetMaintenanceOff}
         onViewDetails={onViewDetails}
         onTriggerTestAlarm={onTriggerTestAlarm}
      />
   )

   const tooltipContent = (
      <CellCardContent
         cellId={cellId}
         bpm={bpm}
         bpmStabilityScore={bpmStabilityScore}
         rpm={rpm}
         rpmStabilityScore={rpmStabilityScore}
         occupied={occupied}
         maintenance={maintenance}
         isCustom={isCustom}
         hasDoorSensor={hasDoorSensor}
         doorSensorLowBattery={doorSensorLowBattery}
         cellStatus={cell}
         userStatus={userStatus}
         typefaceColor={typefaceColor}
         className="cell-card__tooltip-content"
         hideButtons
         onViewDetails={onViewDetails}
      />
   )

   if (isExpanded && size !== CardSize.LARGE) {
      return largeCellCard
   }

   if (size === CardSize.SMALL) {
      return (
         <Tooltip
            classes={{
               tooltip: classNames(
                  "cell-card__tooltip-container",
                  `cell-card--${cellStatus}`,
                  { "cell-card--custom": isCustom }
               ),
            }}
            content={tooltipContent}
         >
            <CellCardSmall
               cellId={cellId}
               bpm={bpm}
               bpmStabilityScore={bpmStabilityScore}
               rpm={rpm}
               rpmStabilityScore={rpmStabilityScore}
               occupied={occupied}
               maintenance={maintenance}
               isCustom={isCustom}
               hasDoorSensor={hasDoorSensor}
               doorSensorLowBattery={doorSensorLowBattery}
               cellStatus={cell}
               userStatus={userStatus}
               typefaceColor={typefaceColor}
               hasAction={hasAction}
               showCellIcon={showCellIcon}
               isClickable={isClickable}
               isCollapsed={isCollapsed}
               onClick={() => onExpandedChange?.(true)}
               onSetVacant={onSetVacant}
               onSetMaintenanceOff={onSetMaintenanceOff}
            />
         </Tooltip>
      )
   }

   if (size === CardSize.MEDIUM) {
      return (
         <Tooltip
            classes={{
               tooltip: classNames(
                  "cell-card__tooltip-container",
                  `cell-card--${cellStatus}`,
                  { "cell-card--custom": isCustom }
               ),
            }}
            content={tooltipContent}
         >
            <span className="cell-card--medium-grid">
               <CellCardMedium
                  cellId={cellId}
                  bpm={bpm}
                  bpmStabilityScore={bpmStabilityScore}
                  rpm={rpm}
                  rpmStabilityScore={rpmStabilityScore}
                  occupied={occupied}
                  maintenance={maintenance}
                  isCustom={isCustom}
                  hasDoorSensor={hasDoorSensor}
                  doorSensorLowBattery={doorSensorLowBattery}
                  cellStatus={cell}
                  userStatus={userStatus}
                  typefaceColor={typefaceColor}
                  hideButtons={hideButtons}
                  hasAction={hasAction}
                  isClickable={isClickable}
                  isCollapsed={isCollapsed}
                  onClick={() => onExpandedChange?.(true)}
                  onSetVacant={onSetVacant}
                  onSetMaintenanceOff={onSetMaintenanceOff}
               />
            </span>
         </Tooltip>
      )
   }

   return largeCellCard
}

export default memo(CellCard, (prevProps, nextProps) => {
   const keys: (keyof CellCardProps)[] = [
      "cellId",
      "bpm",
      "rpm",
      "occupied",
      "isCustom",
      "hasDoorSensor",
      "doorSensorChargeLevel",
      "cellStatus",
      "userStatus",
      "typefaceColor",
      "maintenance",
      "isExpanded",
      "isCollapsed",
   ]
   return keys.every((key) => isEqual(prevProps[key], nextProps[key]))
})
